import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const INITIAL = { label: "", link: "/", parent_id: "", order: 1, visible: true };

export default function AdminMenu() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(INITIAL);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/menu"); setItems(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const topLevel = items.filter((i) => !i.parent_id);
  const childrenOf = (pid) => items.filter((i) => i.parent_id === pid).sort((a, b) => (a.order || 0) - (b.order || 0));

  const startCreate = (parent_id = "") => {
    setEditing(null);
    setForm({ ...INITIAL, parent_id, order: (childrenOf(parent_id).length || topLevel.length) + 1 });
    setOpen(true);
  };
  const startEdit = (it) => { setEditing(it); setForm({ ...INITIAL, ...it, parent_id: it.parent_id || "" }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, parent_id: form.parent_id || null };
      if (editing) await api.put(`/admin/menu/${editing.id}`, payload);
      else await api.post("/admin/menu", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Save failed"); }
  };

  const remove = async (it) => {
    const kids = childrenOf(it.id);
    if (kids.length > 0 && !window.confirm(`"${it.label}" has ${kids.length} submenu item(s). Delete them too?`)) return;
    if (!window.confirm(`Delete "${it.label}"?`)) return;
    // delete children first
    for (const k of kids) { await api.delete(`/admin/menu/${k.id}`); }
    await api.delete(`/admin/menu/${it.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-menu">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h1 className="heading-xl text-3xl">NAVIGATION MENU</h1>
        <button onClick={() => startCreate("")} className="btn-primary text-sm" data-testid="admin-menu-new">
          <Plus className="w-4 h-4" /> New Top-Level Item
        </button>
      </div>
      <div className="bg-[#FFCC00]/20 border-l-4 border-[#E65C00] p-4 mb-6 text-sm">
        <strong>Hierarchical menu.</strong> Add a parent item (e.g. "Products" or "Services"), then click <strong>"+ Add Sub"</strong> on its row to add brand/manufacturer dropdowns under it.
        <br /><br />
        <strong className="text-[#E65C00]">PRODUCT BRAND DROPDOWNS:</strong> Want a separate page for each manufacturer (Bull Machines, JCB, Tata Hitachi)? Add them as <em>sub-items under Products</em> and set their link to <code className="bg-white px-1.5 py-0.5 border border-gray-300">/products?brand=Brand Name</code>. The catalog will auto-filter to that brand — admin still manages all products from one screen.
        <br /><br />
        <strong>Hover-only parent:</strong> If you want clicking "Products" to <em>not</em> navigate (only show dropdown on hover), set its link to <code className="bg-white px-1.5 py-0.5 border border-gray-300">#</code>.
      </div>

      <div className="space-y-3">
        {loading ? <div className="text-center py-8 text-gray-500">Loading...</div>
          : topLevel.length === 0 ? <div className="border border-dashed border-gray-300 py-12 text-center text-gray-500">No menu items yet.</div>
          : topLevel.sort((a, b) => (a.order || 0) - (b.order || 0)).map((it) => {
            const kids = childrenOf(it.id);
            return (
              <div key={it.id} className="bg-white border border-gray-200" data-testid={`menu-item-${it.id}`}>
                <div className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-xs bg-gray-100 px-2 py-1">#{it.order || 0}</span>
                    <div>
                      <div className="font-heading font-bold uppercase tracking-tight">{it.label}</div>
                      <div className="text-xs text-gray-500">{it.link} {it.visible ? "" : "· hidden"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => startCreate(it.id)} className="text-xs uppercase font-bold tracking-widest text-[#E65C00] hover:underline px-2" data-testid={`menu-add-sub-${it.id}`}>+ Add Sub</button>
                    <button onClick={() => startEdit(it)} className="p-1.5 hover:bg-gray-100" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(it)} className="p-1.5 hover:bg-red-50 text-red-600" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {kids.length > 0 && (
                  <div className="border-t border-gray-100 bg-gray-50">
                    {kids.map((k) => (
                      <div key={k.id} className="flex items-center justify-between p-3 pl-12 border-b border-gray-100 last:border-0" data-testid={`menu-sub-${k.id}`}>
                        <div className="flex items-center gap-4">
                          <span className="font-mono text-xs bg-white border border-gray-200 px-1.5 py-0.5">#{k.order || 0}</span>
                          <div>
                            <div className="text-sm font-semibold">{k.label}</div>
                            <div className="text-xs text-gray-500">{k.link} {k.visible ? "" : "· hidden"}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => startEdit(k)} className="p-1.5 hover:bg-gray-100" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => remove(k)} className="p-1.5 hover:bg-red-50 text-red-600" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase">{editing ? "Edit" : "New"} {form.parent_id ? "Sub-Item" : "Menu Item"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Label</label>
              <input className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" placeholder="e.g. Bull Machines" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="menu-form-label" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Link</label>
              <input className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" placeholder="e.g. /products?brand=Bull Machines" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} data-testid="menu-form-link" />
              <div className="text-[10px] text-gray-500 mt-1">Tip: For brand sub-menus use <code>/products?brand=Bull Machines</code> — the catalog filter accepts these params.</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Parent Item (leave blank for top-level)</label>
              <select className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} data-testid="menu-form-parent">
                <option value="">— Top-level —</option>
                {topLevel.filter((t) => !editing || t.id !== editing.id).map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Order</label>
                <input type="number" className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })} data-testid="menu-form-order" />
              </div>
              <label className="flex items-end gap-2 pb-2">
                <input type="checkbox" checked={!!form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} data-testid="menu-form-visible" />
                <span className="text-sm font-bold">Visible</span>
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="btn-outline text-sm"><X className="w-4 h-4" /> Cancel</button>
            <button onClick={save} className="btn-primary text-sm" data-testid="menu-form-save"><Save className="w-4 h-4" /> Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
